import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search, 
  Filter, 
  ArrowRight,
  Droplets,
  Sun,
  Gamepad2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categoriaIcons = {
  agua: Droplets,
  seco: Sun,
  interactivo: Gamepad2,
};

const categoriaLabels = {
  agua: 'Acuático',
  seco: 'Seco',
  interactivo: 'Interactivo',
};

export default function CatalogoPage() {
  const [inflables, setInflables] = useState([]);
  const [filteredInflables, setFilteredInflables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('todos');
  const [ordenFilter, setOrdenFilter] = useState('popularidad');

  useEffect(() => {
    const fetchInflables = async () => {
      try {
        const response = await axios.get(`${API_URL}/inflables?activo=true`);
        setInflables(response.data);
        setFilteredInflables(response.data);
      } catch (error) {
        console.error('Error fetching inflables:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInflables();
  }, []);

  useEffect(() => {
    let result = [...inflables];

    // Search filter
    if (searchTerm) {
      result = result.filter(inf => 
        inf.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inf.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoriaFilter !== 'todos') {
      result = result.filter(inf => inf.categoria === categoriaFilter);
    }

    // Sort
    if (ordenFilter === 'popularidad') {
      result.sort((a, b) => b.popularidad - a.popularidad);
    } else if (ordenFilter === 'precio_asc') {
      result.sort((a, b) => a.precio_base - b.precio_base);
    } else if (ordenFilter === 'precio_desc') {
      result.sort((a, b) => b.precio_base - a.precio_base);
    }

    setFilteredInflables(result);
  }, [searchTerm, categoriaFilter, ordenFilter, inflables]);

  return (
    <PublicLayout>
      {/* Header */}
      <section className="bg-gradient-to-br from-allegra-light via-white to-secondary/10 py-12 bg-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-allegra-dark mb-4">
              Catálogo de Inflables
            </h1>
            <p className="text-allegra-gray text-lg max-w-2xl mx-auto">
              Explora nuestra colección de brincolines y encuentra el perfecto para tu evento
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-allegra-gray" />
                <Input 
                  placeholder="Buscar inflables..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="categoria-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas las categorías</SelectItem>
                  <SelectItem value="seco">Seco</SelectItem>
                  <SelectItem value="agua">Acuático</SelectItem>
                  <SelectItem value="interactivo">Interactivo</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ordenFilter} onValueChange={setOrdenFilter}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="orden-filter">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popularidad">Más populares</SelectItem>
                  <SelectItem value="precio_asc">Precio: Menor a Mayor</SelectItem>
                  <SelectItem value="precio_desc">Precio: Mayor a Menor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Pills */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Button 
              variant={categoriaFilter === 'todos' ? 'default' : 'outline'}
              className="rounded-full"
              onClick={() => setCategoriaFilter('todos')}
              data-testid="filter-todos"
            >
              Todos
            </Button>
            {Object.entries(categoriaLabels).map(([key, label]) => {
              const Icon = categoriaIcons[key];
              return (
                <Button 
                  key={key}
                  variant={categoriaFilter === key ? 'default' : 'outline'}
                  className="rounded-full gap-2"
                  onClick={() => setCategoriaFilter(key)}
                  data-testid={`filter-${key}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Button>
              );
            })}
          </div>

          {/* Results Count */}
          <p className="text-allegra-gray mb-6">
            Mostrando {filteredInflables.length} inflable{filteredInflables.length !== 1 ? 's' : ''}
          </p>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : filteredInflables.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-allegra-gray text-lg">No se encontraron inflables con los filtros seleccionados.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredInflables.map((inflable) => {
                const CategoryIcon = categoriaIcons[inflable.categoria] || Sun;
                return (
                  <Link to={`/inflable/${inflable.id}`} key={inflable.id} data-testid={`catalog-item-${inflable.id}`}>
                    <Card className="overflow-hidden border-0 shadow-lg card-hover bg-white h-full group">
                      <div className="relative h-64">
                        <img 
                          src={inflable.imagen_url || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600'} 
                          alt={inflable.nombre}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <Badge className="absolute top-4 left-4 bg-white/90 text-allegra-dark gap-1">
                          <CategoryIcon className="w-3 h-3" />
                          {categoriaLabels[inflable.categoria]}
                        </Badge>
                        {inflable.popularidad > 80 && (
                          <Badge className="absolute top-4 right-4 bg-accent text-allegra-dark">
                            Popular
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-6">
                        <h3 className="font-heading font-bold text-xl text-allegra-dark mb-2 group-hover:text-primary transition-colors">
                          {inflable.nombre}
                        </h3>
                        <p className="text-allegra-gray text-sm mb-4 line-clamp-2">
                          {inflable.descripcion}
                        </p>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm text-allegra-gray">6 horas desde</span>
                            <p className="font-heading font-bold text-2xl text-primary">
                              ${inflable.precio_base}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-allegra-gray">Anticipo</span>
                            <p className="font-semibold text-allegra-dark">
                              ${inflable.anticipo_requerido}
                            </p>
                          </div>
                        </div>
                        <Button className="w-full mt-4 rounded-full gap-2 group-hover:bg-primary group-hover:text-white" variant="outline">
                          Ver detalles
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
